import { computed, ref, type App, type ComputedRef } from 'vue'

import { usePostHog } from '@foundever/ts-commons/libs'

import type { FeatureFlagOptions, FeatureFlagsCallback } from 'posthog-js'

const noopPosthog = {
  identify: (_id?: string, _props?: Record<string, unknown>) => {},
  capture: (_name: string, _props?: Record<string, unknown>) => {},
  onFeatureFlags: (cb: FeatureFlagsCallback) => {
    // No PostHog configured, so there is nothing to wait for and nothing failed.
    cb([], {}, { errorsLoading: false })
  },
  isFeatureEnabled: (_key: string, _options?: FeatureFlagOptions) => false,
  __loaded: false as boolean,
}

function createClient() {
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY
  const host = import.meta.env.VITE_POSTHOG_HOST
  if (apiKey && host) {
    return usePostHog({
      apiKey,
      host,
      // main.ts sends the pageview once the user is identified.
      posthogConfigs: { capture_pageview: false },
    })
  }
  return {
    posthog: noopPosthog,
    initializeErrorTracking: (_app: App) => {},
  }
}

const client = createClient()

export const posthog = client.posthog
export const initializeErrorTracking = client.initializeErrorTracking

/**
 * Enumeration of PostHog feature-flag keys used in this app.
 * Values must match flags configured in PostHog exactly.
 */
export const PosthogFeatureFlags = {
  HtmlPreviewer: 'html-previewer',
  RagUpload: 'rag-upload',
  EvergptUnwrapped: 'evergpt-unwrapped',
  /** HTML preview panel: publish to web + published badges / links. */
  WebPublishing: 'web-publishing',
  /** Kill switch for the password-protected visibility option when publishing a webpage. */
  WebPublishingPassword: 'web-publishing-password',
  /** Server-side / UI: PowerPoint deck generation from chat. */
  PptsGeneration: 'ppts-generation',
  /** Excel (.xlsx) generation from chat. */
  XlsxGeneration: 'xlsx-generation',
  /** PDF document generation from chat. */
  PdfGeneration: 'pdf-generation',
  MaintenancePage: 'maintenance-page',
  /** PWA install prompt, service worker updates, and related in-app notifications. */
  Pwa: 'pwa',
  /** GPT Knowledge: SharePoint files tab (connect / sync SharePoint files to a GPT). */
  SharePointKnowledge: 'sharepoint-knowledge',
  /** Global kill switch for file uploads in the chat input. */
  FileUploads: 'file-uploads',
  /** Global kill switch for web search in the chat input. */
  SearchWeb: 'search-web',
  /** Global kill switch for image generation in the chat input. */
  ImageGeneration: 'image-generation',
  /** Global kill switch for voice dictation in the chat input. */
  Dictate: 'dictate',
  /** Use on-device browser speech recognition for dictation when supported. */
  DictateBrowserStt: 'dictate-browser-stt',
  /** Periodic conversation data-retention awareness modal. */
  DataRetentionModal: 'data-retention-modal',
  /**
   * Announcements: user-facing active-list fetch / modal queue / built-in
   * fallbacks, and the Admin → Announcements tab. Off skips every announcements
   * request (`GET /announcements` included), shows no modals, and hides the
   * admin tab. The fetch waits until flags settle, so the fail-open default
   * does not call the endpoint before PostHog can say the flag is off.
   */
  Announcements: 'announcements',
  /** Sidebar chat pinning: the Pinned section, the pin/unpin option and reordering. */
  ChatPinning: 'chat-pinning',
  /**
   * Admin LLM model form: offer Azure as a model source. Off leaves LLM Foundry as
   * the only source for new models; a model already saved as Azure keeps it.
   */
  AzureLlmSource: 'azure-llm-source',
  /**
   * Admin → LLM models: show the `gpt-image-1` row. Off hides that deployment
   * (including foundry/Azure-prefixed slugs) from the table and its chat-selector
   * preview. Fail-open when PostHog is not configured.
   */
  AdminGptImage1: 'admin-gpt-image-1',
  /**
   * Last-selected model memory: cross-tab seed (`selected_model_id_v3`) and
   * tab-local pick (`tab_selected_model_id_v3`). Off skips reading and writing
   * both; per-chat pins (`chat_model_ids`) stay as they are. Fail-open when
   * PostHog is not configured (dev / staging).
   */
  RememberLastSelectedModel: 'remember-last-selected-model',
  /**
   * MCP connectors: the Connectors page and its sidebar link, and the connected
   * apps offered in the chat input. Off skips every `/mcp/*` request.
   */
  McpConnectors: 'mcp-connectors',
} as const

export type PosthogFeatureFlagKey =
  (typeof PosthogFeatureFlags)[keyof typeof PosthogFeatureFlags]

/**
 * Flags that switch something **off**. The reverse of {@link PosthogFeatureFlags}:
 * the feature runs unless its flag is explicitly on, so a flag nobody created, an
 * unreachable PostHog and an environment with no PostHog at all each leave it
 * running. For controls that must not depend on a flag having been set up.
 */
export const PosthogKillSwitches = {
  /**
   * Turns off card-number redaction in the chat composer (PCI gate DR-1). Redaction
   * is on by default; this exists to stop it during a false-positive incident
   * without a release. The other text fields have no switch.
   */
  ChdMaskingDisabled: 'chd-masking-disabled',
} as const

export type PosthogKillSwitchKey =
  (typeof PosthogKillSwitches)[keyof typeof PosthogKillSwitches]

/**
 * Whether PostHog has actually delivered flags.
 *
 * `posthog.__loaded` is not that signal: `init()` sets it synchronously, so it stays
 * true even when every network call the library makes is blocked — a tracker blocker
 * or Safari's tracking protection is enough, and `i.posthog.com` is on the common
 * blocklists. On that path posthog-js still runs its flags callback (with
 * `errorsLoading: true`) and still sets its own internal "flags loaded" bit, so
 * `isFeatureEnabled` answers a confident `false` for every key rather than
 * `undefined` — no warning, no way to tell it apart from a flag that is genuinely
 * off. Every kill switch would then read OFF for that user, silently removing file
 * uploads, web search, dictation, pinning and the rest.
 *
 * So track successful delivery here and fail open when it never happened, which is
 * the intent the `!posthog.__loaded` branch below already encodes for the
 * not-configured case.
 */
let hasReceivedFeatureFlags = false

export const isPosthogFlagOn = (
  flag?: PosthogFeatureFlagKey | null,
): boolean => {
  if (flag == null) return true
  if (!posthog.__loaded || !hasReceivedFeatureFlags) return true
  return Boolean(posthog.isFeatureEnabled(flag, { send_event: false }))
}

/**
 * PostHog delivers flags asynchronously, and `isPosthogFlagOn` reads a plain object, so
 * a `computed` wrapping it has nothing reactive to track and keeps whatever answer it
 * gave first. Until the flags land that answer is the permissive default — meaning a
 * kill switch on a surface rendered at startup, like the sidebar, would never fire.
 * Bumping this on delivery gives those computeds something to depend on.
 */
const featureFlagsRevision = ref(0)
posthog.onFeatureFlags((_flags, _variants, context) => {
  // Fires on failure too — only a clean load means the flags below are trustworthy.
  if (!context?.errorsLoading) hasReceivedFeatureFlags = true
  featureFlagsRevision.value += 1
})

/**
 * Reactive `isPosthogFlagOn`: re-evaluates once PostHog delivers or refreshes flags.
 * Prefer this over calling `isPosthogFlagOn` inside a `computed`.
 */
export const usePosthogFlag = (
  flag?: PosthogFeatureFlagKey | null,
): ComputedRef<boolean> =>
  computed(() => {
    void featureFlagsRevision.value
    return isPosthogFlagOn(flag)
  })

/**
 * Whether a kill switch has been thrown. Only a flag PostHog has cleanly delivered
 * as on counts — everything short of that (not configured, not loaded yet, request
 * failed, flag unknown or off) reads as not thrown, so the feature stays on.
 */
export const isPosthogKillSwitchOn = (flag: PosthogKillSwitchKey): boolean => {
  if (!posthog.__loaded || !hasReceivedFeatureFlags) return false
  return Boolean(posthog.isFeatureEnabled(flag, { send_event: false }))
}

/** Reactive `isPosthogKillSwitchOn`: re-evaluates once PostHog delivers or refreshes flags. */
export const usePosthogKillSwitch = (
  flag: PosthogKillSwitchKey,
): ComputedRef<boolean> =>
  computed(() => {
    void featureFlagsRevision.value
    return isPosthogKillSwitchOn(flag)
  })

/**
 * True once PostHog has reported flags (a clean load or a failed one).
 *
 * `usePosthogFlag` is fail-open until that happens, which is right for hiding a
 * control and wrong for a request: a query gated only on the flag fires on the
 * first paint, before PostHog can say the flag is off. Wait on this before
 * calling an endpoint the flag is meant to suppress.
 */
export const usePosthogFlagsSettled = (): ComputedRef<boolean> =>
  computed(() => featureFlagsRevision.value > 0)

/**
 * False while PostHog is configured but nothing has got through, so an event sent
 * now may never arrive: flags still loading, or a tracker blocker. Where PostHog is
 * not configured (local, e2e) nobody is counting events, so this stays true.
 */
export const usePosthogReachable = (): ComputedRef<boolean> =>
  computed(() => {
    void featureFlagsRevision.value
    return !posthog.__loaded || hasReceivedFeatureFlags
  })
